<!-- 宠物领取/更换记录：家长与管理员操作审计。最后更新：2026-11-04 -->
<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick" :type="0"></DepartmentPane>
    </section>
    <section class="dp-right pet-log-page">
      <div class="page-head">
        <p class="dp-dept-hint">
          <span class="dp-dept-hint__label">当前机构</span>
          <span class="dp-dept-hint__value">{{ departmentName || '未选择' }}</span>
        </p>
      </div>

      <div class="filter-bar">
        <el-select v-model="query.semesterId" size="small" clearable placeholder="全部学期" class="filter-bar__semester" @change="handleQuery">
          <el-option v-for="s in semesters" :key="s.semesterId" :value="s.semesterId" :label="s.semesterName + (s.isCurrent ? '（当前）' : '')" />
        </el-select>
        <el-date-picker
          v-model="query.dateRange"
          type="daterange"
          size="small"
          value-format="yyyy-MM-dd"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          :picker-options="rangeShortcuts"
          class="filter-bar__range"
          @change="handleQuery" />
        <el-select v-model="query.actionType" size="small" clearable placeholder="全部操作" class="filter-bar__small" @change="handleQuery">
          <el-option :value="1" label="领取" />
          <el-option :value="2" label="更换" />
        </el-select>
        <el-select v-model="query.operatorType" size="small" clearable placeholder="全部来源" class="filter-bar__small" @change="handleQuery">
          <el-option value="parent" label="家长" />
          <el-option value="admin" label="管理员" />
        </el-select>
        <el-input
          v-model="query.keyword"
          size="small"
          clearable
          placeholder="姓名 / 学号"
          prefix-icon="el-icon-search"
          class="filter-bar__search"
          @keyup.enter.native="handleQuery"
          @clear="handleQuery" />
        <el-button type="primary" size="small" icon="el-icon-search" :loading="loading" @click="handleQuery">查询</el-button>
        <el-button size="small" icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
        <el-button type="warning" plain size="small" icon="el-icon-download" :loading="exporting" @click="exportLog">导出 Excel</el-button>
      </div>

      <div class="tbl-scroll-host">
        <el-table :data="list" v-loading="loading" height="100%" style="width: 100%" class="log-table">
          <el-table-column label="时间" width="156" align="center">
            <template slot-scope="{ row }">
              <div class="cell-stack">
                <span class="num">{{ formatTime(row.createTime) }}</span>
                <span class="muted">{{ row.semesterName || '—' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="学生" min-width="150" align="center">
            <template slot-scope="{ row }">
              <div class="cell-stack">
                <span class="strong ellipsis" :title="row.studentName">{{ row.studentName || '—' }}</span>
                <span class="muted ellipsis" :title="[row.departmentName, row.studentNo].filter(Boolean).join(' · ')">
                  {{ row.departmentName || '—' }}<template v-if="row.studentNo"> · {{ row.studentNo }}</template>
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="变更" min-width="180" align="center">
            <template slot-scope="{ row }">
              <div class="cell-stack">
                <span :class="['action-tag', row.actionType === 2 ? 'is-change' : 'is-claim']">{{ row.actionType === 2 ? '更换' : '领取' }}</span>
                <span v-if="row.actionType === 2" class="pet-flow">
                  <span class="muted">{{ row.fromPetName || '已删除' }}</span>
                  <i class="el-icon-right"></i>
                  <span class="strong">{{ row.toPetName || '已删除' }}</span>
                </span>
                <span v-else class="strong ellipsis" :title="row.toPetName">{{ row.toPetName || '已删除' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="当时快照" min-width="160" align="center">
            <template slot-scope="{ row }">
              <div v-if="hasSnap(row)" class="cell-stack">
                <span class="level-badge">{{ row.levelIndex ? `Lv.${row.levelIndex}${row.levelName ? ' ' + row.levelName : ''}` : '等级 —' }}</span>
                <span class="snap-score">
                  学期 {{ formatScore(row.semesterScore) }}
                  <span class="snap-dot">·</span>
                  <span :class="scoreClass(row.todayScore)">当天 {{ signedScore(row.todayScore) }}</span>
                </span>
              </div>
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>
          <el-table-column label="操作人" min-width="120" align="center">
            <template slot-scope="{ row }">
              <div class="cell-stack">
                <span :class="['source-tag', row.operatorType === 'admin' ? 'is-admin' : 'is-parent']">{{ row.operatorType === 'admin' ? '管理员' : '家长' }}</span>
                <span class="muted ellipsis">{{ row.operatorName || '—' }}</span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-container">
        <el-pagination
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="page.currentPage"
          :page-size="page.pageSize"
          :page-sizes="[20, 50, 100]"
          :total="page.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange" />
      </div>
    </section>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane.vue'
import { getPetChangeLogList, exportPetChangeLogList, getPetSemesterList } from '@/api/modules/pet'
import { resolveActiveSchoolId, treeNodeDeptId, deptIdsEqual } from '@/utils/deptCascaderProps'

/** 日期格式化为 yyyy-MM-dd */
function formatDate (date) {
  const d = new Date(date)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 最近 N 天的日期区间 */
function recentDays (days) {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - (days - 1))
  return [start, end]
}

export default {
  name: 'PetChangeLog',
  components: { DepartmentPane },
  data () {
    return {
      schoolId: 0,
      departmentId: 0,
      departmentName: '',
      loading: false,
      exporting: false,
      list: [],
      semesters: [],
      query: { semesterId: null, dateRange: null, actionType: null, operatorType: '', keyword: '' },
      page: { currentPage: 1, pageSize: 20, total: 0 },
      rangeShortcuts: {
        shortcuts: [
          { text: '今天', onClick: picker => picker.$emit('pick', recentDays(1)) },
          { text: '最近 7 天', onClick: picker => picker.$emit('pick', recentDays(7)) },
          { text: '最近 30 天', onClick: picker => picker.$emit('pick', recentDays(30)) }
        ]
      }
    }
  },
  methods: {
    /** 左侧部门树点击：学校取顶栏当前学校 */
    async treeNodeClick (data) {
      this.departmentId = treeNodeDeptId(data)
      this.departmentName = (data && (data.label || data.Name)) || ''
      const schoolId = resolveActiveSchoolId(this)
      if (schoolId && !deptIdsEqual(schoolId, this.schoolId)) {
        this.schoolId = schoolId
        await this.loadSemesters()
      }
      this.handleQuery()
    },
    async loadSemesters () {
      if (!this.schoolId) return
      const res = await getPetSemesterList({ schoolId: this.schoolId })
      this.semesters = (res.data && res.data.response) || []
      const current = this.semesters.find(x => x.isCurrent)
      this.query.semesterId = current ? current.semesterId : null
    },
    handleQuery () {
      this.page.currentPage = 1
      this.load()
    },
    resetQuery () {
      const current = this.semesters.find(x => x.isCurrent)
      this.query = { semesterId: current ? current.semesterId : null, dateRange: null, actionType: null, operatorType: '', keyword: '' }
      this.handleQuery()
    },
    async load () {
      if (!this.schoolId || !this.departmentId) return
      this.loading = true
      try {
        const range = this.query.dateRange || []
        const res = await getPetChangeLogList({
          schoolId: this.schoolId,
          departmentId: this.departmentId,
          semesterId: this.query.semesterId || null,
          startDate: range[0] || null,
          endDate: range[1] || null,
          actionType: this.query.actionType || null,
          operatorType: this.query.operatorType || '',
          keyword: (this.query.keyword || '').trim(),
          page: this.page.currentPage,
          pageSize: this.page.pageSize
        })
        if (res.data && res.data.success) {
          const data = res.data.response || {}
          this.list = data.data || []
          this.page.total = data.dataCount || 0
        } else {
          this.list = []
          this.$message.error((res.data && res.data.msg) || '查询失败')
        }
      } finally {
        this.loading = false
      }
    },
    handleCurrentChange (val) {
      this.page.currentPage = val
      this.load()
    },
    handleSizeChange (val) {
      this.page.pageSize = val
      this.handleQuery()
    },
    /** 导出当前筛选条件下的全部操作记录。 */
    async exportLog () {
      if (!this.schoolId || !this.departmentId) return this.$message.warning('请先在左侧选择部门')
      this.exporting = true
      const range = this.query.dateRange || []
      try {
        const res = await exportPetChangeLogList({
          schoolId: this.schoolId,
          departmentId: this.departmentId,
          semesterId: this.query.semesterId || null,
          startDate: range[0] || null,
          endDate: range[1] || null,
          actionType: this.query.actionType || null,
          operatorType: this.query.operatorType || '',
          keyword: (this.query.keyword || '').trim()
        })
        if (!res.data || !res.data.size) return this.$message.error('没有可导出的记录')
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.download = `宠物操作记录_${this.query.dateRange ? this.query.dateRange.join('_') : '全部'}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    },
    formatTime (value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').slice(0, 16)
    },
    formatScore (value) {
      if (value === null || value === undefined) return '—'
      const num = Number(value) || 0
      return Number.isInteger(num) ? num : num.toFixed(1)
    },
    signedScore (value) {
      if (value === null || value === undefined) return '—'
      const num = Number(value) || 0
      return num > 0 ? `+${this.formatScore(num)}` : `${this.formatScore(num)}`
    },
    hasSnap (row) {
      return !!(row && (row.levelIndex || row.semesterScore != null || row.todayScore != null))
    },
    scoreClass (value) {
      const num = Number(value) || 0
      return num > 0 ? 'is-up' : num < 0 ? 'is-down' : ''
    },
    formatDate
  }
}
</script>

<style scoped>
.pet-log-page { display: flex; flex-direction: column; min-height: 0; }
.page-head { display: flex; align-items: center; margin-bottom: 8px; }
.page-head .dp-dept-hint { margin: 0; }
.filter-bar { flex: 0 0 auto; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.filter-bar__semester { width: 190px; }
.filter-bar__range { width: 250px !important; }
.filter-bar__small { width: 110px; }
.filter-bar__search { width: 160px; }

.log-table /deep/ .el-table__header th { background: #f8fafc; color: #475569; font-weight: 600; }
.log-table /deep/ .el-table__body td { vertical-align: middle; }
.log-table /deep/ .el-table__row:hover > td { background: #f5f9ff !important; }

.cell-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  line-height: 1.35;
  text-align: center;
}
.snap-dot { margin: 0 4px; color: #cbd5e1; }
.snap-score { font-size: 12px; color: #475569; font-variant-numeric: tabular-nums; white-space: nowrap; }
.snap-score .is-up { color: #15803d; }
.snap-score .is-down { color: #b45309; }
.num { font-variant-numeric: tabular-nums; color: #0f172a; font-size: 13px; }
.muted { color: #64748b; font-size: 12px; }
.strong { color: #0f172a; font-weight: 500; font-size: 13px; }
.ellipsis {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.action-tag,
.source-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
}
.action-tag.is-claim { background: #dcfce7; color: #15803d; }
.action-tag.is-change { background: #eff6ff; color: #1d4ed8; }
.source-tag.is-parent { background: #fef3c7; color: #b45309; }
.source-tag.is-admin { background: #ede9fe; color: #6d28d9; }
.pet-flow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-width: 100%;
  white-space: nowrap;
}
.pet-flow i { color: #94a3b8; flex-shrink: 0; }
.level-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}
</style>
